import { useEffect, useRef, useState } from "react";
import {
  DIALOG_PARAM,
  DIALOG_ID_PARAM,
  getSearchParam,
} from "../services/utils";

/**
 * Syncs a dialog's open/close state with the URL.
 *
 * - Opening pushes ?dialog=<slug>&dialogId=<id> (browser back closes it)
 * - Closing cleans the params
 * - On mount, if the URL already matches, forces the dialog open (deep-link)
 *
 * Returns { effectiveOpen, handleClose } to use instead of the raw open/onClose.
 */
export default function useDialogUrl(
  slug: string | null,
  open: boolean,
  onClose?: () => void,
  id?: string | null
) {
  // Deep-link: if the URL already matches on mount, force open
  const [urlOpened, setUrlOpened] = useState(() => {
    if (!slug) return false;
    if (getSearchParam(DIALOG_PARAM) !== slug) return false;
    if (id != null) return getSearchParam(DIALOG_ID_PARAM) === id;
    return true;
  });

  const effectiveOpen = open || urlOpened;
  const prevOpenRef = useRef(false);
  const prevOpenPropRef = useRef(open);
  const slugRef = useRef(slug);
  slugRef.current = slug;
  const idRef = useRef(id);
  idRef.current = id;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // When the parent explicitly closes (open true→false), reset urlOpened
  // so effectiveOpen becomes false even if the dialog was URL-opened.
  useEffect(() => {
    if (prevOpenPropRef.current && !open) {
      setUrlOpened(false);
    }
    prevOpenPropRef.current = open;
  }, [open]);

  const handleClose = () => {
    setUrlOpened(false);
    const url = new URL(window.location.href);
    const currentSlug = url.searchParams.get(DIALOG_PARAM);
    const currentId = url.searchParams.get(DIALOG_ID_PARAM);
    if (
      currentSlug === slugRef.current &&
      (idRef.current == null || currentId === idRef.current)
    ) {
      url.searchParams.delete(DIALOG_PARAM);
      url.searchParams.delete(DIALOG_ID_PARAM);
      window.history.replaceState({}, "", url.toString());
    }
    onCloseRef.current?.();
  };

  // Sync open state -> URL
  useEffect(() => {
    if (!slug) return;
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = effectiveOpen;

    if (effectiveOpen && !wasOpen) {
      const url = new URL(window.location.href);
      const alreadyMatches =
        url.searchParams.get(DIALOG_PARAM) === slug &&
        (id == null || url.searchParams.get(DIALOG_ID_PARAM) === id);
      if (!alreadyMatches) {
        url.searchParams.set(DIALOG_PARAM, slug);
        if (id != null) url.searchParams.set(DIALOG_ID_PARAM, id);
        window.history.pushState({}, "", url.toString());
      }
    } else if (!effectiveOpen && wasOpen) {
      // Guard: don't clear if another dialog has already pushed its slug/id
      const url = new URL(window.location.href);
      if (
        url.searchParams.get(DIALOG_PARAM) === slug &&
        (id == null || url.searchParams.get(DIALOG_ID_PARAM) === id)
      ) {
        url.searchParams.delete(DIALOG_PARAM);
        url.searchParams.delete(DIALOG_ID_PARAM);
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [effectiveOpen, slug, id]);

  // Browser back: close when the URL no longer matches our slug+id
  useEffect(() => {
    if (!effectiveOpen) return;

    const handlePopState = () => {
      const slugMatch = getSearchParam(DIALOG_PARAM) === slugRef.current;
      const idMatch =
        idRef.current == null ||
        getSearchParam(DIALOG_ID_PARAM) === idRef.current;
      if (!slugMatch || !idMatch) {
        setUrlOpened(false);
        onCloseRef.current?.();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [effectiveOpen]);

  return { effectiveOpen, handleClose };
}
